/**
 * `useWorldCodex` — the campaign codex, on the TanStack Query plane.
 *
 * The codex is a plain read-only browser over `<world>/codex.json`
 * (`GET /api/world/codex`, `decisions/0157`): the server validates it once at boot and never
 * writes it, so it is a request/response fetch with an effectively infinite stale time —
 * "the file is validated at BOOT so it cannot change under a running server." Issuing a new
 * record to the table is an edit to that file (`codex/README.md`'s two-file convention),
 * made from the CLI, not a runtime distinction this hook needs to know about. `cli/src/
 * codex-cli.ts` reads the same route headlessly.
 *
 * **Loading and failure both degrade to the empty codex, not to an error surface.** A world
 * with no `codex.json` legitimately answers an empty entry list, so "no records on file" is
 * already a first-class state the browser renders; collapsing the pending and failed cases
 * into it means ROLODEX.reg has exactly one empty rendering instead of three, and a codex
 * fetch that fails can never take the desktop down with it.
 */
import { useQuery } from "@tanstack/react-query";
import { emptyCodex, type CodexContent } from "@lancer-vtt/domain";
import { fetchWorldCodex } from "../api";

/** Query key for the world codex fetch. */
export const WORLD_CODEX_QUERY_KEY = ["world-codex"] as const;

/** The world's authored codex; empty while pending or if the fetch failed. */
export function useWorldCodex(): CodexContent {
  const query = useQuery({
    queryKey: WORLD_CODEX_QUERY_KEY,
    queryFn: fetchWorldCodex,
    staleTime: Infinity,
  });
  return query.data ?? emptyCodex();
}
